export const CC_READ_MAX_ATTEMPTS = 3;
export const CC_READ_RETRY_DELAYS_MS = Object.freeze([0, 250, 1_000]);

export type CcReadIntent = Readonly<{
  taskId: string;
  mountId: string;
  isCurrent: () => boolean;
  markRead: (taskId: string, signal: AbortSignal) => Promise<void>;
  shouldRetry: (error: unknown) => boolean;
  onRead: () => void;
  onFailure?: (error: unknown) => void;
}>;

export class CcReadCoordinator {
  private current?: Readonly<{ key: string; controller: AbortController }>;

  schedule(intent: CcReadIntent) {
    const key = `${intent.taskId}:${intent.mountId}`;
    if (this.current?.key === key) return;
    this.cancel();
    const controller = new AbortController();
    this.current = { key, controller };
    void this.run(key, controller, intent);
  }

  cancel() {
    this.current?.controller.abort();
    this.current = undefined;
  }

  private async run(
    key: string,
    controller: AbortController,
    intent: CcReadIntent,
  ) {
    for (let attempt = 0; attempt < CC_READ_MAX_ATTEMPTS; attempt += 1) {
      try {
        await wait(CC_READ_RETRY_DELAYS_MS[attempt] ?? 0, controller.signal);
        if (!this.isCurrent(key, controller, intent)) return;
        await intent.markRead(intent.taskId, controller.signal);
        if (!this.isCurrent(key, controller, intent)) return;
        this.current = undefined;
        intent.onRead();
        return;
      } catch (error) {
        if (controller.signal.aborted || !this.isCurrent(key, controller, intent)) return;
        if (attempt + 1 >= CC_READ_MAX_ATTEMPTS || !intent.shouldRetry(error)) {
          this.current = undefined;
          intent.onFailure?.(error);
          return;
        }
      }
    }
  }

  private isCurrent(
    key: string,
    controller: AbortController,
    intent: CcReadIntent,
  ) {
    return !controller.signal.aborted
      && this.current?.key === key
      && this.current.controller === controller
      && intent.isCurrent();
  }
}

function wait(delayMs: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('当前 CC 表单挂载已过期', 'AbortError'));
      return;
    }
    const timer = globalThis.setTimeout(resolve, delayMs);
    signal.addEventListener('abort', () => {
      globalThis.clearTimeout(timer);
      reject(new DOMException('当前 CC 表单挂载已过期', 'AbortError'));
    }, { once: true });
  });
}
